import { Link, useSearchParams } from 'react-router';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert } from '@/components/ui';
import { SaleStatusCell } from '@/components/SaleStatusCell';
import { MonthPicker, RefreshButton } from '@/components/widgets';
import { formatBRL, formatCompetencia, formatDayMonth } from '@/lib/format';
import type { SaleRow } from '@/lib/types';
import { useApi } from '@/lib/useApi';
import { useMonth } from '@/lib/useMonth';

interface SalesResponse {
  competencia: string;
  escopo: 'EQUIPE' | 'PROPRIAS';
  vendas: (SaleRow & { vendedorId: string | null })[];
  semVendedor: SaleRow[];
  totais: { quantidade: number; valorBruto: string; aguardandoCusto: number };
}

/** Lista de vendas do mês. Vendedor: as próprias. Gestor: todas, com filtro por vendedor. */
export function SalesPage() {
  const me = useCurrentUser();
  const [mes, setMes] = useMonth();
  const [params, setParams] = useSearchParams();
  const vendedor = params.get('vendedor');
  const soPendentes = params.get('filtro') === 'pendentes';
  const qs = new URLSearchParams({ competencia: mes, ...(vendedor ? { vendedor } : {}) });
  const { data, error, reload } = useApi<SalesResponse>(`/sales?${qs.toString()}`, 60_000);
  const equipe = data?.escopo === 'EQUIPE';

  const setParam = (k: string, v: string | null) =>
    setParams((p) => {
      if (v) p.set(k, v);
      else p.delete(k);
      return p;
    });

  const vendas = (data?.vendas ?? []).filter((v) => !soPendentes || v.status === 'AGUARDANDO_CUSTO');
  const nomes = [...new Map((data?.vendas ?? []).filter((v) => v.vendedorId).map((v) => [v.vendedorId as string, v.vendedorNome ?? ''])).entries()];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{equipe ? 'Vendas da equipe' : 'Minhas vendas'}</h1>
          <p className="muted">
            {formatCompetencia(mes)}
            {data && (
              <>
                {' '}
                · {data.totais.quantidade} venda(s) · {formatBRL(data.totais.valorBruto)} · {data.totais.aguardandoCusto} sem custo
              </>
            )}
          </p>
        </div>
        <div className="row">
          <MonthPicker value={mes} onChange={setMes} />
          <RefreshButton onDone={() => void reload()} />
        </div>
      </header>

      <div className="row filters">
        <label className="check">
          <input type="checkbox" checked={soPendentes} onChange={(e) => setParam('filtro', e.target.checked ? 'pendentes' : null)} />
          Só vendas sem custo
        </label>
        {equipe && (
          <select aria-label="Filtrar por vendedor" value={vendedor ?? ''} onChange={(e) => setParam('vendedor', e.target.value || null)}>
            <option value="">Todos os vendedores</option>
            {nomes.map(([id, nome]) => (
              <option key={id} value={id}>
                {nome}
              </option>
            ))}
            {vendedor && !nomes.some(([id]) => id === vendedor) && <option value={vendedor}>Vendedor selecionado</option>}
          </select>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      <section className="card flush">
        {!data ? (
          <p className="muted pad">Carregando…</p>
        ) : vendas.length === 0 ? (
          <p className="muted pad">{soPendentes ? 'Nenhuma venda sem custo. Tudo em dia!' : 'Nenhuma venda neste mês ainda.'}</p>
        ) : (
          <SalesTable vendas={vendas} showVendedor={equipe} meId={me.id} />
        )}
      </section>

      {data && equipe && !vendedor && data.semVendedor.length > 0 && (
        <section className="card flush">
          <div className="pad-h">
            <h2>Sem vendedor ligado ({data.semVendedor.length})</h2>
            <p className="muted small">
              Vendas sem vendedor no Conta Azul, ou com vendedor ainda não ligado a um usuário
              {me.role === 'ADMIN' && (
                <>
                  {' '}
                  (ligue em <Link to="/usuarios">Usuários</Link>)
                </>
              )}
              . Não contam para metas nem comissões.
            </p>
          </div>
          <SalesTable vendas={data.semVendedor} showVendedor={false} meId={me.id} />
        </section>
      )}
    </div>
  );
}

function SalesTable({ vendas, showVendedor, meId }: { vendas: (SaleRow & { vendedorId?: string | null })[]; showVendedor: boolean; meId: string }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Venda</th>
            <th scope="col">Cliente</th>
            {showVendedor && <th scope="col">Vendedor</th>}
            <th scope="col" className="num">Valor</th>
            <th scope="col" className="num">Custo</th>
            <th scope="col" className="num">Lucro</th>
            <th scope="col" className="num">Comissão</th>
            <th scope="col">Situação</th>
          </tr>
        </thead>
        <tbody>
          {vendas.map((v) => (
            <tr key={v.id} className={v.status === 'CANCELADA' ? 'row--off row--strike' : undefined}>
              <td>
                <Link to={`/vendas/${v.id}`}>#{v.numero ?? '—'}</Link> <span className="muted small">{formatDayMonth(v.dataVenda)}</span>
              </td>
              <td>{v.clienteNome}</td>
              {showVendedor && <td>{v.vendedorNome ?? '—'}</td>}
              <td className="num">{formatBRL(v.valorBruto)}</td>
              <td className="num">{v.custoTotal ? formatBRL(v.custoTotal) : '—'}</td>
              <td className={`num ${v.lucro && v.lucro.startsWith('-') ? 'neg' : ''}`}>{v.lucro ? formatBRL(v.lucro) : '—'}</td>
              <td className="num">{v.comissaoValor ? formatBRL(v.comissaoValor) : '—'}</td>
              <td>
                <SaleStatusCell sale={v} mine={v.vendedorId === meId || Boolean(v.editavel)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
